import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { displayDescription } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import { Page, NamedLink, NamedRedirect, LayoutSingleColumn } from '../../components';
import WishlistButton from '../../components/WishlistButton/WishlistButton';
import { useWishlist } from '../../context/WishlistContext';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import WedlyCategoryListingDetails from './WedlyCategoryListingDetails';
import ListingHeroImageZoom from './ListingHeroImageZoom/ListingHeroImageZoom';
import SectionMapMaybe from './SectionMapMaybe';
import css from './ListingPage.module.css';

const { UUID } = sdkTypes;
const FALLBACK_GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&q=80',
  'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&q=80',
  'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&q=80',
  'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&q=80',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
];

const IconTrustPayments = () => (
  <svg className={css.trustIconSvg} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2.8 19 5.7v6.1c0 4.7-3.1 8.1-7 9.4-3.9-1.3-7-4.7-7-9.4V5.7l7-2.9Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 12.5v-1.2a2.5 2.5 0 0 1 5 0v1.2M10.5 12.5h3v3.5h-3v-3.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconTrustPickup = () => (
  <svg className={css.trustIconSvg} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IconTrustCircular = () => (
  <svg className={css.trustIconSvg} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M7 7.5A7.5 7.5 0 0 1 17 9.5M17 16.5A7.5 7.5 0 0 1 7 14.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M7 4.5v3h3M17 19.5v-3h-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 8.5v2.5l2 1.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TRUST_ITEMS = [
  {
    key: 'secure-payments',
    title: 'Secure Payments & Escrow',
    desc: 'Protected payments released only after confirmed handover.',
    icon: IconTrustPayments,
  },
  {
    key: 'verified-pickup',
    title: 'Verified Local Pickup',
    desc: 'Location-first discovery makes meetups easier and more trusted.',
    icon: IconTrustPickup,
  },
  {
    key: 'circular-economy',
    title: 'Sustainable Circular Economy',
    desc: 'Loved pieces continue their story instead of ending after one day.',
    icon: IconTrustCircular,
  },
];

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [activeImg, setActiveImg] = useState(0);
  const { isWishlisted: isListingWishlisted } = useWishlist();

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    relatedListings = [],
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
  } = currentListing.attributes;

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showDescription = displayDescription(foundListingTypeConfig);
  const descriptionPlain = typeof description === 'string' ? description.trim() : '';

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const authorId = currentAuthor?.id?.uuid;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);
  const sellerName =
    authorDisplayName || intl.formatMessage({ id: 'ListingPage.sellerFallbackName' });
  const sellerProfileImageVariants = ensuredAuthor?.profileImage?.attributes?.variants || {};
  const sellerProfileImage =
    sellerProfileImageVariants['square-small2x']?.url ||
    sellerProfileImageVariants['square-small']?.url ||
    null;
  const sellerInitial = sellerName?.trim()?.charAt(0)?.toUpperCase() || 'S';
  const sellerVerifiedDetails = [
    intl.formatMessage({ id: 'ListingPage.sellerVerifiedIdentity' }),
    intl.formatMessage({ id: 'ListingPage.sellerVerifiedEmail' }),
    intl.formatMessage({ id: 'ListingPage.sellerTrustedPickup' }),
  ];
  const categoryLabel =
    publicData?.categoryLevel2 ||
    publicData?.categoryLevel1 ||
    publicData?.listingType ||
    'Ceremony Decor';
  const productImages = (currentListing?.images || [])
    .map(img => {
      const variants = img?.attributes?.variants || {};
      return (
        variants['scaled-xlarge']?.url ||
        variants['scaled-large']?.url ||
        variants['scaled-medium']?.url ||
        variants['scaled-small']?.url ||
        null
      );
    })
    .filter(Boolean);
  const galleryImages = productImages.length ? productImages : FALLBACK_GALLERY_IMAGES;
  const safeActiveImg = Math.min(activeImg, Math.max(galleryImages.length - 1, 0));
  const locationText = [
    publicData?.locationSuburb || publicData?.city,
    publicData?.locationState || publicData?.state,
  ]
    .filter(Boolean)
    .join(', ');
  const relatedListingsToShow = (relatedListings || [])
    .filter(l => l?.id?.uuid !== currentListing?.id?.uuid)
    .slice(0, 4);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

  const descriptionBlock = placementClass => {
    if (!showDescription || !descriptionPlain) {
      return null;
    }
    return (
      <div className={`${css.listingDescription} ${placementClass}`}>
        <h2 className={css.listingDescriptionHeading}>
          <FormattedMessage id="ListingPage.descriptionTitle" />
        </h2>
        <div className={css.listingDescriptionText}>
          {richText(descriptionPlain, {
            linkify: true,
            longWordMinLength: 20,
            longWordClass: css.longWord,
            breakChars: '/',
          })}
        </div>
      </div>
    );
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.productPageContainer}>
          <div className={css.productBreadcrumbs}>
            <span>Home</span>
            <span>/</span>
            <span>Shop</span>
            <span>/</span>
            <span>{categoryLabel}</span>
            <span>/</span>
            <span className={css.productBreadcrumbCurrent}>{title}</span>
          </div>

          <div className={css.productLayout}>
            <div className={css.galleryColumn}>
              <ListingHeroImageZoom
                images={galleryImages}
                activeIndex={safeActiveImg}
                onActiveIndexChange={setActiveImg}
                alt={title}
                onManageDisableScrolling={onManageDisableScrolling}
                overlay={
                  <WishlistButton
                    listing={currentListing}
                    className={`${css.wishlistButton} ${
                      isListingWishlisted(currentListing?.id?.uuid)
                        ? css.wishlistButtonActive
                        : ''
                    }`}
                    size="lg"
                    allowAnonymous
                    requireAuth={false}
                  />
                }
              />
              <div className={css.thumbRow}>
                {galleryImages.map((img, i) => (
                  <img
                    key={`listing-thumb-${i}`}
                    src={img}
                    alt={`View ${i + 1}`}
                    onClick={() => setActiveImg(i)}
                    className={`${css.thumbImage} ${
                      safeActiveImg === i ? css.thumbImageActive : ''
                    }`}
                  />
                ))}
              </div>

              {descriptionBlock(css.listingDescriptionGallery)}
            </div>

            <div className={css.detailColumn}>
              <div className={css.categoryPill}>{categoryLabel}</div>

              <h1 className={css.productTitle}>{title}</h1>

              <div className={css.priceAndLocation}>
                <span className={css.productPrice}>{formattedPrice}</span>
                <span className={css.productLocation}>
                  {locationText
                    ? `${locationText} · Local pickup available`
                    : 'Local pickup available'}
                </span>
              </div>

              <div className={css.sellerVerifiedCard}>
                <div className={css.sellerVerifiedHeader}>
                  <div className={css.sellerIdentity}>
                    {sellerProfileImage ? (
                      <img
                        src={sellerProfileImage}
                        alt={sellerName}
                        className={css.sellerAvatar}
                      />
                    ) : (
                      <span className={css.sellerAvatarFallback} aria-hidden="true">
                        {sellerInitial}
                      </span>
                    )}
                    <span className={css.sellerVerifiedName}>
                      <FormattedMessage
                        id="ListingPage.sellerVerifiedBy"
                        values={{
                          sellerName: (
                            <NamedLink
                              className={css.sellerShopLink}
                              name="ShopFrontPage"
                              params={{ id: ensuredAuthor.id.uuid }}
                            >
                              {sellerName}
                            </NamedLink>
                          ),
                        }}
                      />
                    </span>
                  </div>
                  <span className={css.sellerVerifiedBadge}>
                    <FormattedMessage id="ListingPage.sellerVerifiedBadge" />
                  </span>
                </div>
                <div className={css.sellerVerifiedList}>
                  {sellerVerifiedDetails.map(item => (
                    <div key={item} className={css.sellerVerifiedItem}>
                      <span className={css.sellerVerifiedCheck}>✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {descriptionBlock(css.listingDescriptionDetail)}

              <WedlyCategoryListingDetails
                publicData={publicData}
                categoryConfiguration={config.categoryConfiguration}
                intl={intl}
                variant="specCard"
              />
              {/* <div className={css.storyCard}>
                <h3 className={css.storyTitle}>
                  {publicData?.listingStoryTitle}
                </h3>
                <p className={css.storyText}>{publicData?.listingStoryDescription}</p>
              </div> */}

              <div className={css.ctaStack}>
                <button onClick={() => handleOrderSubmit({})} className={css.primaryButton}>
                  {intl.formatMessage({ id: 'ListingPage.secureTreasure' })}
                </button>
                <button onClick={onContactUser} className={css.secondaryButton}>
                  {intl.formatMessage({ id: 'ListingPage.messageSeller' })}
                </button>
              </div>
            </div>
          </div>

          <SectionMapMaybe
            rootClassName={css.productLocationSection}
            geolocation={geolocation}
            publicData={publicData}
            listingId={currentListing.id}
            mapsConfig={config.maps}
          />

          <div className={css.trustSection}>
            <div className={css.trustRow}>
              {TRUST_ITEMS.map(item => {
                const TrustIcon = item.icon;
                return (
                  <div key={item.key} className={css.trustCard}>
                    <div className={css.trustIcon} aria-hidden="true">
                      <TrustIcon />
                    </div>
                    <div>
                      <div className={css.trustTitle}>{item.title}</div>
                      <div className={css.trustDesc}>{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {relatedListingsToShow.length > 0 ? (
            <div className={css.relatedSection}>
              <div className={css.relatedHeader}>
                <h2 className={css.relatedTitle}>You may also love</h2>
                {authorId ? (
                  <NamedLink
                    name="ProfilePage"
                    params={{ id: authorId }}
                    className={css.relatedLink}
                  >
                    View more →
                  </NamedLink>
                ) : null}
              </div>
              <div className={css.relatedGrid}>
                {relatedListingsToShow.map((item, i) => {
                  const relatedPublicData = item?.attributes?.publicData || {};
                  const relatedTitle = item.attributes?.title || '';
                  const relatedPrice = priceData(
                    item.attributes?.price,
                    config.currency,
                    intl
                  ).formattedPrice;
                  const relatedLocation =
                    [
                      relatedPublicData?.locationSuburb || relatedPublicData?.city,
                      relatedPublicData?.locationState || relatedPublicData?.state,
                    ]
                      .filter(Boolean)
                      .join(', ') ||
                    intl.formatMessage({ id: 'ListingPage.relatedLocationFallback' });
                  const relatedImages = (item?.images || [])
                    .map(img => {
                      const variants = img?.attributes?.variants || {};
                      return (
                        variants['scaled-large']?.url ||
                        variants['scaled-medium']?.url ||
                        variants['scaled-small']?.url ||
                        variants['listing-card']?.url ||
                        variants['listing-card-2x']?.url ||
                        null
                      );
                    })
                    .filter(Boolean);
                  const relatedImage =
                    relatedImages[0] ||
                    FALLBACK_GALLERY_IMAGES[i % FALLBACK_GALLERY_IMAGES.length];
                  const listingLinkParams = {
                    slug: createSlug(relatedTitle),
                    id: item.id.uuid,
                  };
                  return (
                    <div key={item.id.uuid} className={css.relatedCard}>
                      <NamedLink
                        name="ListingPage"
                        params={listingLinkParams}
                        className={css.relatedListingLink}
                      >
                        <div className={css.relatedImageWrap}>
                          <img
                            src={relatedImage}
                            alt={relatedTitle}
                            className={css.relatedImage}
                          />
                          <WishlistButton
                            listing={item}
                            className={css.relatedWishButton}
                            size="sm"
                            allowAnonymous
                            requireAuth={false}
                          />
                        </div>
                        <div className={css.relatedItemTitle}>{relatedTitle}</div>
                        <div className={css.relatedItemPrice}>{relatedPrice}</div>
                        <div className={css.relatedItemLocation}>{relatedLocation}</div>
                      </NamedLink>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    relatedListingRefs,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getRelatedListings = refs => getMarketplaceEntities(state, refs || []);

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    relatedListings: getRelatedListings(relatedListingRefs),
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
